import clsx from 'clsx';
import {
  type ForwardedRef,
  type HTMLAttributes,
  type MouseEvent,
  type PropsWithChildren,
  forwardRef,
} from 'react';
import type { Spacing } from './layout';
import { isDefined } from '@/utils/assertions';


export interface VStackProps {
  horizontalAlignment?: 'start' | 'center' | 'full' | 'end';
  verticalAlignment?: 'start' | 'center' | 'full' | 'end';
  fullWidth?: boolean;
  fullHeight?: boolean;
  flex?: 'min' | 'expand';
  gap?: Spacing;
  margin?: Spacing;
  padding?: Spacing;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  className?: string;
}

/**
 * Vertical layout:
 *
 * @example
 * // +-----------------------------+
 * // |        +--------------+     |
 * // |        |     ONE      |     |
 * // |        +--------------+     |
 * // |        +--------------+     |
 * // |        |     TWO      |     |
 * // |        +--------------+     |
 * // |        +--------------+     |
 * // |        |              | ^   |
 * // | PARENT |              | |   |
 * // |        |    spacer    | |   |
 * // |        |              | |   |
 * // |        |              | v   |
 * // |        +--------------+     |
 * // |        +--------------+     |
 * // |        |     THREE    |     |
 * // |        +--------------+     |
 * // +-----------------------------+
 *
 * @example
 * <VStack horizontalAlignment="center" verticalAlignment="full" fullWidth gap="8px" padding="4px">
 *   <div>ONE</div>
 *   <div>TWO</div>
 *   <Spacer />
 *   <div>THREE</div>
 * </VStack>
 */
export const VStack = forwardRef(
  (
    props: PropsWithChildren<VStackProps> & HTMLAttributes<HTMLDivElement>,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const {
      horizontalAlignment = 'full',
      verticalAlignment = 'full',
      fullWidth = false,
      fullHeight = false,
      flex = 'min',
      gap,
      margin,
      padding,
      children,
      onClick,
      className,
      ...otherProps
    } = props;

    const componentClassName = clsx(
      [
        'd-flex flex-column flex-nowrap overflow-hidden',
        isDefined(gap) ? `gap-${gap}` : undefined,
        isDefined(margin) ? `m-${margin}` : undefined,
        isDefined(padding) ? `p-${padding}` : undefined,
        className,
      ],
      {
        'align-items-start': horizontalAlignment === 'start',
        'align-items-center': horizontalAlignment === 'center',
        'align-items-between': horizontalAlignment === 'full',
        'align-items-end': horizontalAlignment === 'end',
        'justify-content-start': verticalAlignment === 'start',
        'justify-content-center': verticalAlignment === 'center',
        'justify-content-between': verticalAlignment === 'full',
        'justify-content-end': verticalAlignment === 'end',
        'flex-grow-0 flex-shrink-0': flex === 'min',
        'flex-fill': flex === 'expand',
        'w-100': fullWidth,
        'h-100': fullHeight,
      },
    );

    return (
      <div ref={ref} onClick={onClick} className={componentClassName} {...otherProps}>
        {children}
      </div>
    );
  },
);
